import React, { useMemo } from "react";

import {
  ExperienceDto,
  HistoryDto,
  OrganizationDto,
} from "@intelligems/ig-types";
import { useIsMutating } from "@tanstack/react-query";

import { useGetHistory } from "../../../../api/histories/get-history";
import {
  checkAllScopes,
  checkBooleanScope,
  checkSubscriptionStatus,
} from "../../../../components/ScopeWrappers/helpers";
import { ScopedComponentWithDisable } from "../../../../components/ScopeWrappers/ScopedComponentWithDisable";

import { ActionExperienceButton } from "./ActionExperienceButton";

interface StartExperienceButtonProps {
  rowExperience: ExperienceDto;
  currentExperience?: ExperienceDto;
  experiencesList: ExperienceDto[];
  organization: OrganizationDto;
  onStartButtonClick: (
    rowExperience: ExperienceDto,
    history: HistoryDto[],
    isSubscriptionStartActive?: boolean
  ) => void;
}

export const StartExperienceButton = ({
  experiencesList,
  organization,
  rowExperience,
  currentExperience,
  onStartButtonClick,
}: StartExperienceButtonProps) => {
  const history = useGetHistory({
    body: undefined,
    pathParams: { experienceId: rowExperience.id },
    queryParams: undefined,
  });
  const isStartExperimentMutating = useIsMutating({
    mutationKey: ["startExperience"],
  });
  const isEndExperimentMutating = useIsMutating({
    mutationKey: ["endExperience"],
  });
  const isPauseExperimentMutating = useIsMutating({
    mutationKey: ["pauseExperience"],
  });
  const isSubscriptionActive = checkSubscriptionStatus();
  const isShippingAlreadyActive: ExperienceDto[] = experiencesList
    .filter((experience) => experience.status === "started")
    .filter((experience) => experience.testTypes.hasTestShipping);
  const canStartPricing = checkBooleanScope(
    "start_price_experiment",
    organization.configurations
  );
  const canStartShipping = checkBooleanScope(
    "start_shipping_experiment",
    organization.configurations
  );
  const canStartCampaign = checkBooleanScope(
    "start_discount_experiment",
    organization.configurations
  );
  const canStartContent = checkBooleanScope(
    "start_content_experiment",
    organization.configurations
  );
  const isIncorrectPlan = useMemo(() => {
    let testType = "";

    if (rowExperience.type === "pricing" && !canStartPricing) {
      testType = "pricing";
    } else if (rowExperience.type === "shipping" && !canStartShipping) {
      testType = "shipping";
    } else if (
      rowExperience.category === "personalization" &&
      !canStartCampaign
    ) {
      testType = "campaign";
    } else if (rowExperience.type.includes("content") && !canStartContent) {
      return "content";
    }
    if (testType.length > 0) {
      return `Please upgrade your plan to run a ${testType} test`;
    } else return null;
  }, [rowExperience]);

  return (
    <ScopedComponentWithDisable
      debugId={"experiment-list-button"}
      adminDisable={
        rowExperience.testTypes.hasTestShipping &&
        rowExperience.status !== "started" &&
        isShippingAlreadyActive.length > 0
      }
      customDisabledTooltipMessage={
        !isSubscriptionActive
          ? "Please approve billing before starting an experiment"
          : rowExperience.testTypes.hasTestShipping &&
              isShippingAlreadyActive.length > 0 &&
              rowExperience.status !== "started"
            ? "Only one shipping test can be run at a time. End your current shipping test to start a new one."
            : (isIncorrectPlan ?? undefined)
      }
      isEnabled={checkAllScopes([
        isSubscriptionActive!,
        rowExperience.testTypes.hasTestPricing
          ? canStartPricing
          : rowExperience.testTypes.hasTestShipping &&
              isShippingAlreadyActive.length > 0 &&
              rowExperience.status !== "started"
            ? false
            : rowExperience.testTypes.hasTestShipping
              ? canStartShipping
              : rowExperience.testTypes.hasTestCampaign
                ? canStartCampaign
                : canStartContent,
      ])}
    >
      <ActionExperienceButton
        isLoading={
          history.isLoading ||
          (Boolean(
            isStartExperimentMutating ||
              isEndExperimentMutating ||
              isPauseExperimentMutating
          ) &&
            currentExperience?.id === rowExperience.id)
        }
        onClick={() => {
          onStartButtonClick(
            rowExperience,
            history?.data?.history ?? [],
            isSubscriptionActive
          );
        }}
        disabled={true}
        action={"start"}
        experience={rowExperience}
      />
    </ScopedComponentWithDisable>
  );
};
